/**
 * Hook for managing the carrier-portal session JWT.
 *
 * The backend issues a 14-day HMAC-SHA256 JWT after the carrier verifies
 * their magic-link token (see /api/carrier-auth/verify-token). This hook:
 *   - persists the token + decoded metadata to localStorage
 *   - exposes the current session (or null if not signed in)
 *   - provides a setSession() to store a new session after verification
 *   - provides signOut() to clear it
 *
 * The token is NOT validated client-side. The backend re-verifies on every
 * /api/carrier/* request (and also re-checks the underlying CarrierContact
 * is still approved + active). Client-side validity is a UX optimization —
 * we don't show "signed in" UI for an expired token even though the backend
 * would reject anyway.
 *
 * Why localStorage and not a cookie:
 *   - The frontend is on dockscheduler.symbia.com, the API is at the same
 *     origin (via the Static Web App proxy), so no cross-origin issues.
 *   - localStorage is simpler than http-only cookie wrangling and the trust
 *     boundary is the same either way (carrier portal is exposed to the
 *     internet regardless).
 *   - If/when we add CSRF concerns, we can switch to a same-site cookie
 *     by changing this hook alone.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'symbia-carrier-session-v1';

export interface CarrierSession {
    sessionToken: string;
    expiresAt: string;        // ISO timestamp
    displayName: string | null;
    carrierCode: string;
    email: string;
    /**
     * ⭐ F212 — which portal this session is (the same sign-in serves both). Absent on a
     * session stored before F212, which was always a carrier's.
     */
    kind?: 'Carrier' | 'Client';
    clientCode?: string | null;
    clientName?: string | null;
}

/**
 * ⭐ F215 — ONE EMAIL, SEVERAL ACCOUNTS (a carrier and a client, or two clients). The sign-in
 * returns a session per account; they are kept here so the header can switch between them
 * without another email. ⚠️ Same storage rules as the session itself; signing out clears both.
 */
const ACCOUNTS_KEY = 'symbia-portal-accounts-v1';

export function readPortalAccounts(): CarrierSession[] {
    try {
        const raw = localStorage.getItem(ACCOUNTS_KEY);
        if (!raw) return [];
        const list = JSON.parse(raw) as CarrierSession[];
        return Array.isArray(list)
            ? list.filter((a) => a && a.sessionToken && a.expiresAt && new Date(a.expiresAt).getTime() > Date.now())
            : [];
    } catch {
        return [];
    }
}

export function writePortalAccounts(list: CarrierSession[]): void {
    try {
        if (list.length > 1) localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list));
        else localStorage.removeItem(ACCOUNTS_KEY);
    } catch { /* storage unavailable: the switch is simply not offered */ }
}

/** What an account is called in the switch and the chooser. */
export const accountLabel = (a: CarrierSession): string =>
    a.kind === 'Client' ? `${a.clientName || a.clientCode || 'Client'} (client)` : `${a.carrierCode || 'Carrier'} (carrier)`;

/** ⭐ F212: the portal a session belongs to ('carrier' for a pre-F212 session). */
export const portalOf = (s: CarrierSession | null | undefined): 'carrier' | 'client' =>
    s?.kind === 'Client' ? 'client' : 'carrier';

/** Read the persisted session, or null if absent / corrupted / expired. */
function readSession(): CarrierSession | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CarrierSession;
        if (!parsed.sessionToken || !parsed.expiresAt) return null;
        if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
            // Expired client-side. Clear so next read returns null too.
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        // Bad JSON or other corruption — treat as no session.
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
}

function writeSession(session: CarrierSession): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function clearSession(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ACCOUNTS_KEY);
}

/**
 * React hook exposing the current carrier session.
 *
 * Returns:
 *   - session: the current session, or null if signed out
 *   - setSession: persist a new session (called from verify page on success)
 *   - signOut: clear the session and trigger a re-render
 *
 * Listens for the 'storage' event so signing out in one tab also signs out
 * any other open tabs.
 */
/**
 * ⭐ F215: the same-tab signal. Each component holds its own copy of the session, and React
 * Router keeps the SAME CarrierAppShell when /carrier/verify moves to the dashboard, so the
 * header kept the signed-out copy it read on the verify page (no name, no Sign out, no account
 * switch) until a reload. A change in this tab now tells every holder; 'storage' covers other tabs.
 */
const SESSION_EVENT = 'symbia-portal-session-changed';

export function useCarrierSession() {
    const [session, setSessionState] = useState<CarrierSession | null>(() => readSession());

    // Cross-tab sync: another tab clearing the session updates this one too.
    // ⭐ F215: and the same tab (SESSION_EVENT), for the shell that outlives the verify page.
    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) {
                setSessionState(readSession());
            }
        };
        const here = () => setSessionState(readSession());
        window.addEventListener('storage', handler);
        window.addEventListener(SESSION_EVENT, here);
        return () => {
            window.removeEventListener('storage', handler);
            window.removeEventListener(SESSION_EVENT, here);
        };
    }, []);

    const setSession = useCallback((s: CarrierSession) => {
        writeSession(s);
        setSessionState(s);
        window.dispatchEvent(new Event(SESSION_EVENT));
    }, []);

    const signOut = useCallback(() => {
        clearSession();
        setSessionState(null);
        window.dispatchEvent(new Event(SESSION_EVENT));
    }, []);

    return { session, setSession, signOut };
}

/**
 * Standalone token-getter for code outside React components (e.g. the API
 * client). Returns the JWT if a session exists, or null.
 */
export function getCarrierToken(): string | null {
    const s = readSession();
    return s?.sessionToken ?? null;
}
